import React from "react";
import { MobileStackHeader } from "../../../ForBrands/ui/mobile/MobileShell";
import { AutosaveBar, HubGroup, SectionRow } from "../../../ForBrands/ui/mobile/MobileUI";
import { HELP_ACTION } from "../../../ForBrands/ui/mobile/support";
import { GROUPS, sectionProgress, sectionSummary } from "./editHubModel";
import { EditLayoutContext, NavIcon, ProfileStrength, Ring } from "./editHubPieces";

// Edit profile on phones (Figma ATHLETE MODE V2 mobile, "Edit profile · Hub
// model (v2)" 2084:1026, D-e1). The editor is a stack route, so the Athlete
// Mode shell draws no chrome and every screen here brings its own header:
// the back chevron, the screen title and "Help". With no section open the
// hub lists the sections with their progress (2084:1028); a section is a
// pushed screen whose fields save on their own, with the AutosaveBar and its
// Done at the bottom (2085:1213 ...). AthleteEditHub keeps the state and the
// saving; these are its phone screens.
//
// Each screen's title is also its visually hidden h1. `headingRef` points at
// it (tabIndex -1), so AthleteEditHub can move focus there when the screen
// changes. Card titles under it are h2 (HubCard reads the phone layout), so
// the outline skips no level.

const PHONE_LAYOUT = Object.freeze({ phone: true });

const Header = ({ title, onBack }) => (
  <MobileStackHeader leading="back" onLeading={onBack} title={title} action={HELP_ACTION} />
);

// The header over a loading, error or signed-out state, so there is always a
// way back.
export const PhoneEditFrame = ({ title = "Edit profile", onBack, children }) => (
  <div className="flex w-full flex-col bg-white">
    <Header title={title} onBack={onBack} />
    <div className="flex flex-col px-6 pt-5 pb-8">{children}</div>
  </div>
);

// 00 · Hub: the strength card, then the sections under their group overlines
// (ProfileSectionRow · Mobile: icon tile, title, support line, ring, chevron;
// the Privacy setting has no ring). `context` renders above the strength card.
// `saveError` is the message of a save that failed after the athlete left its
// section (Back saves on the way out): the hub has no AutosaveBar, so it says
// so here instead of dropping the change without a word.
export const PhoneHub = ({ sections, athlete, names, codeMap, hub, whose, onOpen, onBack, context = null, saveError = "", headingRef }) => (
  <div className="flex w-full flex-col bg-white">
    <Header title="Edit profile" onBack={onBack} />
    <div className="flex flex-col gap-[22px] px-6 pt-5 pb-[calc(32px+env(safe-area-inset-bottom))]">
      <h1 ref={headingRef} tabIndex={-1} className="sr-only">Edit profile</h1>
      {saveError && (
        <div role="alert" className="flex flex-col gap-[2px] rounded-[16px] border border-bm-mutedbg bg-white px-[18px] py-4 break-words">
          <span className="bm-label-l text-bm-critical">Your last change was not saved.</span>
          <span className="bm-body-m text-bm-muted">{saveError}</span>
        </div>
      )}
      {context}
      <ProfileStrength hub={hub} whose={whose} onOpen={onOpen} />
      {GROUPS.filter((g) => sections.some((s) => s.group === g)).map((g) => {
        const rows = sections.filter((s) => s.group === g);
        return (
          <HubGroup key={g} label={g}>
            {rows.map((s, i) => {
              const p = sectionProgress(s, athlete, names);
              return (
                <SectionRow
                  key={s.id}
                  icon={<NavIcon src={s.icon} />}
                  title={s.label}
                  support={sectionSummary(s, athlete, names, codeMap)}
                  ring={s.setting ? null : <Ring size={32} pct={p.pct} complete={p.complete} />}
                  onClick={() => onOpen(s.id)}
                  last={i === rows.length - 1}
                />
              );
            })}
          </HubGroup>
        );
      })}
    </div>
  </div>
);

// Section head on phones (2085:1241): the 44px ring with its value in
// bm-label-l, "N of M done" and the description. The title sits in the top
// bar and the save status in the AutosaveBar.
export const PhoneSectionHead = ({ section, progress }) => (
  <div className="flex items-center gap-[14px]">
    <Ring size={44} pct={progress.pct} complete={progress.complete} labelClass="bm-label-l" />
    <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
      <span className="bm-label-l text-bm-ink">{progress.done} of {progress.total} done</span>
      {section.desc && <p className="bm-body-m text-bm-muted">{section.desc}</p>}
    </div>
  </div>
);

// A section screen: header, head (none for the Privacy setting), the section
// body with the phone pieces (EditLayoutContext) and 56px fields
// (.bm-phone-form), and the fixed AutosaveBar. The body leaves room for the bar.
export const PhoneSection = ({ section, progress, status, error, onBack, onDone, doneBusy = false, headingRef, children }) => (
  <EditLayoutContext.Provider value={PHONE_LAYOUT}>
    <div className="flex w-full flex-col bg-white">
      <Header title={section.label} onBack={onBack} />
      <div className="bm-phone-form flex flex-col gap-6 px-6 pt-5 pb-[calc(150px+env(safe-area-inset-bottom))]">
        <h1 ref={headingRef} tabIndex={-1} className="sr-only">{section.label}</h1>
        {!section.setting && <PhoneSectionHead section={section} progress={progress} />}
        {children}
      </div>
      <AutosaveBar status={status} error={error} onDone={onDone} busy={doneBusy} />
    </div>
  </EditLayoutContext.Provider>
);
